import {
	BigNum,
	BN,
	BulkAccountLoader,
	VelocityClient,
	LAMPORTS_EXP,
	MarketType,
	SwapMode,
	TokenFaucet,
	UserStatsAccount,
	ZERO,
	UnifiedSwapClient,
	SwapQuote,
} from '@velocity-exchange/sdk';
import {
	UIMarket,
	UISerializableOrderActionRecord,
	UISerializableLiquidationRecord,
	OpenPosition,
	UI_ORDER_TYPES,
	MarketId,
	RpcEndpoint,
	NEW_ACCOUNT_DONATION,
	NEW_ACCOUNT_BASE_COST,
	NEW_ACCOUNT_BASE_RENT,
	MIN_LEFTOVER_SOL,
	MAIN_POOL_ID,
} from '@velocity-exchange/common';
import { Wallet } from '@solana/wallet-adapter-react';
import {
	BaseMessageSignerWalletAdapter,
	WalletReadyState,
} from '@solana/wallet-adapter-base';
import {
	Connection,
	PublicKey,
	Transaction,
	VersionedTransaction,
} from '@solana/web3.js';
import EventEmitter from 'events';
import produce from 'immer';
import VelocityAppEvents from 'src/utils/VelocityAppEvents';
import StrictEventEmitter from 'strict-event-emitter-types';
import { create } from 'zustand';
import { SEMANTIC_STATUS } from '../../components/Utils/SemanticChip';
import { PageRoute } from '../../constants/constants';
import WalletConnectionState from '../../utils/WalletConnectionState';
import { ModalParamsLinkedListNode } from 'src/components/Modals/types';
import { VAULT_TERMS_AND_CONDITIONS_DO_NOT_SHOW_AGAIN_KEY } from 'src/constants/vaults/misc';
import { AccountCreationCostBreakdown } from 'src/hooks/useAccountCreationCost';
import { BorrowMarketData } from 'src/hooks/useSyncBorrowLendBalances';
import { POSTHOG_CONFIG } from 'src/providers/posthog/posthogConfig';
import { ITradeForm } from './types/tradeForm';
import { getIsZeroFeesActiveForMarket } from 'src/utils/zeroFees';
import { MobileBottomToggleTab } from 'src/components/TradePageGrid/mobile/types';
import { Z_INDEX } from 'src/constants/zIndex';
import { TradeTableTabValue } from 'src/components/TradePageTable/hooks/useTradePageTableTabs';

export type VelocityAppEventEmitter = StrictEventEmitter<
	EventEmitter,
	VelocityAppEvents
>;

const INITIAL_WALLET_STATE: VelocityStore['wallet'] = {
	current: {
		adapter: undefined,
		readyState: WalletReadyState.Unsupported,
	},
	currentPublicKeyString: null,
	currentSolBalance: BigNum.zero(LAMPORTS_EXP),
	isAppKit: false,
	isPrivyNativeAuth: false,
	connectionState: new WalletConnectionState(),
	overrideTxVersion: undefined,
};

export interface VelocityStore {
	latestDeployId: string;
	connection: {
		rpc: RpcEndpoint | null;
		accountLoader: BulkAccountLoader | null;
		current: Connection | null;
	};
	wallets: Wallet[];
	isEmulatingAccount: boolean;
	wallet: {
		current: {
			adapter: BaseMessageSignerWalletAdapter | undefined;
			readyState: WalletReadyState;
		};
		currentPublicKeyString: string | null;
		currentSolBalance: BigNum;
		isAppKit: boolean;
		isPrivyNativeAuth: boolean;
		lastRouteBeforeConnect?: string;
		connectionState: WalletConnectionState;
		overrideTxVersion?: 'legacy' | 0;
	};
	tradeForm: ITradeForm;
	selectedMarket: {
		name: string;
		current: UIMarket | null;
		marketId: MarketId;
	};
	setTradeFormMaxLeverageSelected: (value: boolean) => void;
	set: (x: (s: VelocityStore) => void) => void;
	get: () => VelocityStore;
	velocityClient: {
		isSubscribed: boolean;
		client?: VelocityClient;
	};
	swapClient: UnifiedSwapClient | null;
	userStatsAccount: UserStatsAccount | undefined;
	faucet: TokenFaucet | undefined;
	loadingElements: {
		[key in UIElementKeys]?: {
			isLoading: boolean;
			callback?: () => void;
		};
	};
	marketTradeHistory: {
		market: UIMarket | null;
		trades: UISerializableOrderActionRecord[];
		mostRecentTradeScore: number;
	};
	ordersAwaitingPnlModalDisplay: Record<
		string,
		{ openPositionInfo: OpenPosition; orderId: number; isIsolated?: boolean }
	>;
	/**
	 * Array of signed txes to auto-cancel existing orders in 1 or more markets
	 * Sent out by the useAutoCancelExistingOrdersListener hook once all positions in marketIndexesOfOrders have been closed
	 */
	ordersAwaitingAutoCancel: {
		marketIndexesOfOrders: number[];
		signedCancelExistingOrdersTx: Transaction | VersionedTransaction;
	}[];
	/* Use same logic to automatically settle pnl after a close */
	pnlsAwaitingAutoSettle: {
		marketIndexesOfPnls: number[];
		signedSettlePnlsTx: Transaction | VersionedTransaction;
	}[];
	countryCode: string | undefined;
	isGeoblocked: boolean;
	geolocationHistory: Array<string>;
	modals: ModalParamsLinkedListNode | null;
	currentPageRoute: PageRoute;
	tradePageTable: {
		currentTab: TradeTableTabValue;
		tradeHistoryMarketFilter: string;
	};
	popups: {
		overlay: {
			show: boolean;
			zIndex: number;
		};
	};
	authorityParam: string | null;
	referrerParam: string | null;
	referrerParamFailed: boolean;
	velocityStatus: {
		mainStatus: {
			status: SEMANTIC_STATUS;
			message: string;
		};
	};
	appEventEmitter: VelocityAppEventEmitter;
	borrowLendData: BorrowMarketData[];
	rpcUrl: string;
	currentBulkAccountLoader: BulkAccountLoader | undefined;
	referredByAuthority: PublicKey | undefined;
	/* Swap page params */
	swap: {
		fromMarketIndex: number;
		toMarketIndex: number;
		poolId: number;
		swapDetails?: {
			userKey: string;
			amount: BN;
			fromMarketIndex: number;
			toMarketIndex: number;
			swapMode: SwapMode;
			quote: SwapQuote;
			onlyDirectRoutes?: boolean;
			slippageBps?: number;
			maxAccounts?: number;
		};
	};
	pollingMultiplier: number;
	allLiquidationHistory: {
		liquidations: UISerializableLiquidationRecord[];
		totalCount: number;
		loading: boolean;
		initialHistoryLoaded: boolean;
	};
	statusBars: {
		showNewVersionStatusBar: boolean;
	};
	tradePageTableFilters: {
		showOnlyCurrentMarket?: boolean;
		showSpotMarkets?: boolean; // only for orders table
		showPerpMarkets?: boolean;
	};
	overviewPageTableFilters: {
		showOnlyCurrentMarket?: boolean;
		showPerpMarkets?: boolean;
		showSpotMarkets?: boolean;
	};
	bankruptcyStats: {
		'24h': {
			totalAmount: number | undefined;
			totalCount: number | undefined;
			ifPayment: number | undefined;
			socialLoss: number | undefined;
			usersSocialLoss: number;
		};
	};
	liquidationStats: {
		'24h': {
			totalAmount: number;
			totalCount: number;
		};
		'30d': {
			totalAmount: number;
			totalCount: number;
		};
	};
	postHog: {
		enabled: boolean;
	};
	[VAULT_TERMS_AND_CONDITIONS_DO_NOT_SHOW_AGAIN_KEY]: boolean;
	accountCreationCost: AccountCreationCostBreakdown;
	listenForSwapEvents: boolean;
	displayDatesAsTimestamp: boolean;
	/**
	 * Whether the currently active mobile trade page view is "trade" or "chart".
	 *
	 * `undefined` when on desktop.
	 */
	activeMobileView: MobileBottomToggleTab | undefined;

	/**
	 * Cache for isolated position deposit simulations
	 * Key: `${marketIndex}|${oraclePrice6Digits}|${baseAssetAmount}|${leverage}`
	 */
	isolatedDepositCache: Record<string, { deposit: string; timestamp: number }>;
}

const DEFAULT_TRADE_FORM: ITradeForm = {
	side: 'buy',
	leadSide: 'quote',
	slippageTolerance: 1.0,
	orderType: UI_ORDER_TYPES.market.value,
	baseSizeStringValue: '',
	quoteSizeStringValue: '',
	priceBoxStringValue: '',
	secondaryPriceBoxStringValue: '',
	reduceOnly: false,
	postOnly: false,
	immediateOrCancel: false,
	priceImpact: {
		marketIndex: 0,
		marketType: MarketType.PERP,
		entryPrice: ZERO,
		priceImpact: ZERO,
		baseAvailable: ZERO,
		bestPrice: ZERO,
		worstPrice: ZERO,
		priceImpactInputBaseSize: ZERO,
		showPriceEstimateOracleDivergenceWarning: false,
		exceedsLiquidity: false,
	},
	maxLeverageSelected: false,
	isTpSlCheckboxChecked: false,
	bracketOrders: undefined,
	savedLimitPrice: null,
	isFlippingPosition: false,
	scaledOrders: {
		orderCount: 2,
		sizeDistribution: 'flat',
	},
	isSwiftEnabled: false,
	leverageAfterTrade: 0,
};

const SOL_PERP_MARKET_CONFIG = {
	marketIndex: 0,
	marketName: 'SOL-PERP',
	baseAssetSymbol: 'SOL',
};

const BTC_PERP_MARKET_CONFIG = {
	marketIndex: 1,
	marketName: 'BTC-PERP',
	baseAssetSymbol: 'BTC',
};

export const DEFAULT_MARKET_CONFIG = getIsZeroFeesActiveForMarket({
	marketIndex: 1,
	isPerp: true,
})
	? BTC_PERP_MARKET_CONFIG
	: SOL_PERP_MARKET_CONFIG;

const useVelocityStore = create<VelocityStore>((set, get): VelocityStore => {
	const immerSet = (fn: (s: VelocityStore) => void) => set(produce(fn) as any);

	return {
		latestDeployId: '',
		connection: {
			rpc: null,
			accountLoader: null,
			current: null,
		},
		isEmulatingAccount: false,
		popups: {
			overlay: {
				show: false,
				zIndex: Z_INDEX.popup,
			},
		},
		tradeForm: DEFAULT_TRADE_FORM,
		wallets: [],
		wallet: INITIAL_WALLET_STATE,
		setTradeFormMaxLeverageSelected: (value: boolean) => {
			immerSet((s) => {
				// @ts-expect-error :: It's OK to write to this readonly field internally. This just forces people to be intentional with how they change these values.
				s.tradeForm.maxLeverageSelected = value;
			});
		},
		set: immerSet,
		get: () => get(),
		velocityClient: {
			isSubscribed: false,
			client: undefined,
		},
		swapClient: null,
		userStatsAccount: undefined,
		faucet: undefined,
		selectedMarket: {
			name: DEFAULT_MARKET_CONFIG.marketName,
			current: UIMarket.createPerpMarket(DEFAULT_MARKET_CONFIG.marketIndex),
			marketId: new MarketId(
				DEFAULT_MARKET_CONFIG.marketIndex,
				MarketType.PERP
			),
		},
		loadingElements: {},
		marketTradeHistory: {
			market: {} as UIMarket,
			trades: [],
			mostRecentTradeScore: 0,
		},
		ordersAwaitingPnlModalDisplay: {},
		ordersAwaitingAutoCancel: [],
		pnlsAwaitingAutoSettle: [],
		countryCode: undefined,
		isGeoblocked: false,
		geolocationHistory: [],
		modals: null,
		currentPageRoute: PageRoute.trade,
		tradePageTable: {
			currentTab: 'positions',
			tradeHistoryMarketFilter: '',
		},
		authorityParam: null,
		referrerParam: null,
		referrerParamFailed: false,
		velocityStatus: {
			mainStatus: {
				status: 'green',
				message: '',
			},
		},
		appEventEmitter: new EventEmitter(),
		borrowLendData: [],
		rpcUrl: '',
		currentBulkAccountLoader: undefined,
		referredByAuthority: undefined,
		swap: {
			fromMarketIndex: 0,
			toMarketIndex: 1,
			poolId: MAIN_POOL_ID,
		},
		pollingMultiplier: 1,
		allLiquidationHistory: {
			liquidations: [],
			totalCount: 0,
			loading: false,
			initialHistoryLoaded: false,
		},
		statusBars: {
			showNewVersionStatusBar: false,
		},
		tradePageTableFilters: {},
		overviewPageTableFilters: {},
		bankruptcyStats: {
			'24h': {
				totalAmount: 0,
				totalCount: 0,
				ifPayment: 0,
				socialLoss: 0,
				usersSocialLoss: 0,
			},
		},
		liquidationStats: {
			'24h': {
				totalAmount: 0,
				totalCount: 0,
			},
			'30d': {
				totalAmount: 0,
				totalCount: 0,
			},
		},
		postHog: {
			enabled: POSTHOG_CONFIG.enabled,
		},
		[VAULT_TERMS_AND_CONDITIONS_DO_NOT_SHOW_AGAIN_KEY]: false,
		accountCreationCost: {
			loaded: false,
			totalCost: NEW_ACCOUNT_BASE_COST,
			newAccountDonation: NEW_ACCOUNT_DONATION,
			baseAccountRent: NEW_ACCOUNT_BASE_RENT,
			extraRent: NEW_ACCOUNT_BASE_COST.sub(NEW_ACCOUNT_BASE_RENT).sub(
				NEW_ACCOUNT_DONATION
			),
			minSolToDeposit: NEW_ACCOUNT_BASE_COST.add(MIN_LEFTOVER_SOL),
			minSolForFees: MIN_LEFTOVER_SOL,
		},
		listenForSwapEvents: true,
		displayDatesAsTimestamp: false,
		activeMobileView: undefined,
		isolatedDepositCache: {},
	};
});

export default useVelocityStore;
